/*
 * File: cell-action-fail.ts
 * Created: 2026-02-04
 * Author: Val Liu
 */

import { Component, Prop, Watch } from "vue-property-decorator";
import CellActionBase from "@root/components/table/elements/body/cell-action/base/cell-action-base";

@Component
export default class CellActionFail extends CellActionBase {
    @Prop({ type: Boolean, default: false })
    showDetail!: boolean;

    showModal = false;
    failureMessage = "";

    get defaultLabel(): string {
        return this.label ?? "w_TranslationJobStatusFailed";
    }

    get failDetailTitle(): string {
        return "w_TranslationJobFailDetailTitle";
    }

    @Watch("row", { immediate: true, deep: true })
    handleRowChange() {
        const visible = String(this.row?.status ?? "").toUpperCase() === "FAILED";
        this.setVisible(visible);
    }

    get fileName(): string {
        const name = String(this.row?.name ?? "").trim();
        return name || "-";
    }

    get fallbackMessage(): string {
        const message = String(this.row?.errorMessage ?? "").trim();
        return message || this.$_("w_TranslationJobStatusFailed");
    }

    get detailMessage(): string {
        if (!this.showDetail) return "";
        return String(this.row?.errorMessage ?? "").trim();
    }

    get resolveMessage(): string {
        const status = String(this.row?.errorStatus ?? "").toUpperCase();
        if (status !== "RESOLVED") return "";
        return String(this.row?.resolveMessage ?? "").trim();
    }

    get reasonType(): "timeout" | "unsupported" | "critical" {
        const status = String(this.row?.errorStatus ?? "").toUpperCase();
        if (status === "UNSUPPORTED") return "unsupported";
        if (status === "TIMEOUT") return "timeout";
        if (status === "CRITICAL") return "critical";
        return this.resolveReasonType(this.failureMessage || this.fallbackMessage);
    }

    get reasonTitleKey(): string {
        if (this.reasonType === "timeout") return "w_TranslationJobFailReasonTimeoutTitle";
        if (this.reasonType === "unsupported") {
            return "w_TranslationJobFailReasonUnsupportedTitle";
        }
        return "w_TranslationJobFailReasonCriticalTitle";
    }

    get reasonDetailKey(): string {
        if (this.reasonType === "timeout") return "w_TranslationJobFailReasonTimeoutDetail";
        if (this.reasonType === "unsupported") {
            return "w_TranslationJobFailReasonUnsupportedDetail";
        }
        return "w_TranslationJobFailReasonCriticalDetail";
    }

    get suggestionLines(): string[] {
        if (this.reasonType === "timeout") {
            return ["w_TranslationJobFailSuggestionRetryLine"];
        }
        if (this.reasonType === "unsupported") {
            return ["w_TranslationJobFailSuggestionFixFormatLine"];
        }
        return ["w_TranslationJobFailSuggestionStopLine"];
    }

    async handleClick(event: Event) {
        if (!this.isVisible) {
            event.preventDefault();
            return;
        }

        const message = await this.fetchFailureMessage();
        this.failureMessage = message;
        this.showModal = true;
    }

    private async fetchFailureMessage(): Promise<string> {
        const fallback = this.fallbackMessage;
        const id = this.row?.id;
        if (!id) return fallback;

        try {
            const response = await this.$server.R("translation-job" as any, {
                where: { id },
            });
            const row =
                (response as any)?.results?.[0] ??
                (Array.isArray(response) ? response[0] : null);
            const message = String(row?.errorMessage ?? "").trim();
            return message || fallback;
        } catch (error) {
            this.console?.error?.(error);
            if ((error as any)?.handled) return fallback;
            return fallback;
        }
    }

    private resolveReasonType(message: string): "timeout" | "unsupported" | "critical" {
        const text = String(message || "").toLowerCase();
        if (
            text.includes("invalid office document") ||
            text.includes("invalid office") ||
            text.includes("unsupported file") ||
            text.includes("corrupt")
        ) {
            return "unsupported";
        }
        if (
            text.includes("dify request failed") ||
            text.includes("timeout") ||
            text.includes("timed out") ||
            text.includes("ecconn") ||
            text.includes("socket") ||
            text.includes(" 500") ||
            text.includes(" 502") ||
            text.includes(" 503") ||
            text.includes(" 504")
        ) {
            return "timeout";
        }
        return "critical";
    }
}
